import { type MouseEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { FilterField, DataTableColumn } from '@/shared/components/entities'
import { type CardConfig } from '@/shared/components/entities/EntityListPage'
import { AssetHoldingListPage } from '@/modules/inventory/holdings/AssetHoldingListPage'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { matchesSearch } from '@/lib/search'
import {
  getAssetHolding,
  listAssetHoldingLocations,
  updateAssetHolding,
  AssetHoldingLocationFormModal,
  type AssetHoldingLocation,
  type AssetHolding,
  type AssetOption,
  type AssetSubgroupOption,
} from '@/modules/inventory/holdings'
import {
  listPoultryHoldings,
  getPoultryTypeOptions,
  createAssetFromTemplate,
  listPoultrySubgroups,
  type PoultryTypeOption,
} from '@/modules/poultry/poultryInventoryService'
import {
  createAssetHoldingWithInitialMovement,
  deleteAssetHoldingWithMovements,
  transferAssetHoldingLocation,
  updateAssetHoldingWithAdjustmentMovement,
} from '@/modules/inventory/movements'
import { listParties } from '@/modules/registrations/contacts/contactsService'
import type { Party } from '@/modules/registrations/contacts/contactsTypes'
import { BatchFormModal, type BatchFormData } from '@/modules/inventory/holdings/batches/BatchFormModal'
import { AssetFormModal } from '@/modules/inventory/holdings/batches/AssetFormModal'
import { BatchAdjustmentModal } from '@/modules/inventory/holdings/batches/BatchAdjustmentModal'
import {
  BatchLocationTransferModal,
  type BatchLocationTransferFormData,
} from '@/modules/inventory/holdings/batches/BatchLocationTransferModal'
import { BatchMediaModal } from '@/modules/inventory/holdings/batches/BatchMediaModal'
import { BatchAttachmentModal } from '@/modules/inventory/holdings/batches/BatchAttachmentModal'
import {
  getBatchMedia,
  getBatchMediaSummaries,
  getBatchMediaUrl,
  type BatchMediaPreview,
  type BatchMediaSummary,
} from '@/modules/inventory/holdings/batches/batchMediaService'
import {
  getBatchAttachmentSummaries,
  type BatchAttachmentSummary,
} from '@/modules/inventory/holdings/batches/batchAttachmentService'
import { EntityMediaIndicator } from '@/shared/media'

interface BatchAdjustmentFormData {
  new_initial_quantity: string
  new_total_cost: string
}

type BatchHolding = AssetHolding & {
  mediaSummary?: BatchMediaSummary
  attachmentSummary?: BatchAttachmentSummary
}

const CONTEXT_KEY = 'poultry'
const INITIAL_MOVEMENT_TYPE = 'acquisition'
const BIRTH_DATE_ATTR = 'birth_date'
const BIRTH_DATE_SOURCE_ATTR = 'birth_date_source'
const PURCHASE_AGE_MONTHS_ATTR = 'purchase_age_months'
const PURCHASE_AGE_DAYS_ATTR = 'purchase_age_days'

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  active: 'success',
  closed: 'info',
  sold: 'warning',
  slaughtered: 'danger',
  transferred: 'info',
}

export function BatchesPage() {
  const { currentUnit } = useUnit()
  const { t, locale } = useTranslation()

  const [assetOptions, setAssetOptions] = useState<PoultryTypeOption[]>([])
  const [assetSubgroups, setAssetSubgroups] = useState<AssetSubgroupOption[]>([])
  const [parties, setParties] = useState<Party[]>([])
  const [locations, setLocations] = useState<AssetHoldingLocation[]>([])
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [assetsLoading, setAssetsLoading] = useState(true)
  const [showAssetModal, setShowAssetModal] = useState(false)
  const [assetModalSubgroupId, setAssetModalSubgroupId] = useState('')
  const [mediaBatch, setMediaBatch] = useState<AssetHolding | null>(null)
  const [attachmentBatch, setAttachmentBatch] = useState<AssetHolding | null>(null)

  const [adjustmentBatch, setAdjustmentBatch] = useState<AssetHolding | null>(null)
  const [adjustmentFormData, setAdjustmentFormData] = useState<BatchAdjustmentFormData>({
    new_initial_quantity: '',
    new_total_cost: '',
  })
  const [adjustmentFormErrors, setAdjustmentFormErrors] = useState<Record<string, string>>({})
  const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false)
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null)
  const [locationTransferBatch, setLocationTransferBatch] = useState<AssetHolding | null>(null)
  const [locationTransferFormData, setLocationTransferFormData] = useState<BatchLocationTransferFormData>({
    location_id: '',
    quantity: '',
    occurred_at: new Date().toISOString().slice(0, 10),
    reason: '',
  })
  const [locationTransferFormErrors, setLocationTransferFormErrors] = useState<Record<string, string>>({})
  const [locationTransferSubmitting, setLocationTransferSubmitting] = useState(false)
  const [locationTransferError, setLocationTransferError] = useState<string | null>(null)

  const unitId = currentUnit?.id
  const tc = (key: string) => t(`registrations.batches.${key}`)
  const locationCreateResolverRef = useRef<((location: AssetHoldingLocation | null) => void) | null>(null)

  useEffect(() => {
    if (!unitId) return

    setAssetsLoading(true)

    Promise.all([
      getPoultryTypeOptions(unitId),
      listPoultrySubgroups(),
      listParties(unitId),
      listAssetHoldingLocations(unitId),
    ])
      .then(([options, subgroupList, partyList, locationList]) => {
        setAssetOptions(options)
        setAssetSubgroups(subgroupList)
        setParties(partyList)
        setLocations(locationList)
      })
      .catch((error) => {
        console.error('[BatchesPage] Failed to load dependencies:', error)
      })
      .finally(() => setAssetsLoading(false))
  }, [unitId])

  if (!unitId) {
    return <div className="p-6 text-red-600">{tc('messages.unitNotSelected')}</div>
  }

  const statusLabels: Record<string, string> = {
    active: tc('status.active'),
    closed: tc('status.closed'),
    sold: tc('status.sold'),
    slaughtered: tc('status.slaughtered'),
    transferred: tc('status.transferred'),
  }
  const ageLabels = {
    year: tc('record.fields.ageYear'),
    years: tc('record.fields.ageYears'),
    month: tc('record.fields.purchaseAgeMonth'),
    months: tc('record.fields.purchaseAgeMonths'),
    day: tc('record.fields.purchaseAgeDay'),
    days: tc('record.fields.purchaseAgeDays'),
    separator: tc('record.fields.purchaseAgeSeparator'),
  }
  const formatBatchAge = (holding: AssetHolding) => formatCurrentAge(holding, ageLabels)
  const getHoldingSubgroupId = (holding: AssetHolding) =>
    holding.asset?.subgroup_id ??
    assetOptions.find((option) => option.value === holding.asset_id)?.subgroup_id ??
    null
  const getHoldingSubgroupLabel = (holding: AssetHolding) => {
    const subgroupId = getHoldingSubgroupId(holding)
    return assetSubgroups.find((subgroup) => subgroup.id === subgroupId)?.name ?? holding.asset?.subgroup?.name ?? '—'
  }

  const filterFields: FilterField[] = [
    {
      key: 'name',
      label: tc('list.columns.name'),
      type: 'text',
      placeholder: tc('list.searchPlaceholder'),
    },
    {
      key: 'status',
      label: tc('list.columns.status'),
      type: 'select',
      options: Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'asset_subgroup_id',
      label: tc('record.fields.assetSubgroup'),
      type: 'select',
      options: [
        { value: '', label: tc('record.fields.assetSubgroupAll') },
        ...assetSubgroups.map((subgroup) => ({ value: subgroup.id, label: subgroup.name })),
      ],
    },
    {
      key: 'location_id',
      label: tc('record.fields.location'),
      type: 'select',
      options: [
        { value: '', label: tc('record.fields.locationAll') },
        ...locations.map((location) => ({ value: location.id, label: location.name })),
      ],
    },
  ]

  const tableColumns: DataTableColumn<AssetHolding>[] = [
    {
      key: 'name',
      label: tc('list.columns.name'),
      render: (holding) => (
        <div>
          <div className="font-medium text-slate-900 dark:text-white">
            {holding.name ?? holding.asset?.name ?? '—'}
          </div>

          {holding.asset && (
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {holding.asset.name}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'quantity',
      label: tc('list.columns.quantity'),
      render: (holding) => (
        <span className="font-mono text-slate-700 dark:text-slate-300">
          {Number(holding.quantity).toLocaleString(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
          })}{' '}
          {holding.uom}
        </span>
      ),
    },
    {
      key: 'asset_subgroup',
      label: tc('record.fields.assetSubgroup'),
      render: (holding) => (
        <span className="text-slate-600 dark:text-slate-400">
          {getHoldingSubgroupLabel(holding)}
        </span>
      ),
    },
    {
      key: 'initial_quantity',
      label: tc('list.columns.initial_quantity'),
      render: (holding) => (
        <span className="text-slate-600 dark:text-slate-400">
          {holding.initial_quantity != null
            ? `${Number(holding.initial_quantity).toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 3,
              })} ${holding.uom}`
            : '—'}
        </span>
      ),
    },
    {
      key: 'acquired_at',
      label: tc('list.columns.acquired_at'),
      render: (holding) =>
        holding.acquired_at ? (
          <span className="text-slate-600 dark:text-slate-400">
            {new Date(`${holding.acquired_at}T00:00:00`).toLocaleDateString(locale)}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'age',
      label: tc('list.columns.age'),
      render: (holding) => (
        <span className="text-slate-600 dark:text-slate-400">
          {formatBatchAge(holding)}
        </span>
      ),
    },
    {
      key: 'location',
      label: tc('record.fields.location'),
      render: (holding) => (
        <span className="text-slate-600 dark:text-slate-400">
          {holding.location?.name ?? 'â€”'}
        </span>
      ),
    },
    {
      key: 'media',
      label: tc('list.columns.media'),
      render: (holding) => (
        <EntityMediaIndicator
          summary={(holding as BatchHolding).mediaSummary}
          label={tc('actions.media')}
          emptyLabel={tc('media.empty')}
          onOpen={() => setMediaBatch(holding)}
        />
      ),
    },
    {
      key: 'attachments',
      label: tc('list.columns.attachments'),
      render: (holding) => (
        <BatchAttachmentIndicator
          count={(holding as BatchHolding).attachmentSummary?.count ?? 0}
          label={tc('actions.attachments')}
          onOpen={() => setAttachmentBatch(holding)}
        />
      ),
    },
    {
      key: 'status',
      label: tc('list.columns.status'),
      render: (holding) => {
        const variant = STATUS_VARIANTS[holding.status] ?? 'info'

        const colorMap: Record<string, string> = {
          success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
          warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
          danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
          info: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
        }

        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorMap[variant]}`}>
            {statusLabels[holding.status] ?? holding.status}
          </span>
        )
      },
    },
  ]

  const labels = {
    title: tc('list.title'),
    description: tc('list.description'),
    newButton: tc('list.newButton'),
    searchPlaceholder: tc('list.searchPlaceholder'),
    emptyTitle: tc('list.empty.title'),
    emptyDescription: tc('list.empty.description'),
    emptyActionLabel: tc('list.empty.actionLabel'),
    modalTitleNew: tc('record.new'),
    modalTitleEdit: tc('record.edit'),
    modalTitleView: tc('record.view'),
    recordTitle: tc('record.new'),
    deleteTitle: tc('record.delete'),
    deleteMessage: tc('record.deleteConfirm'),
    deleteButton: tc('actions.delete'),
    cancelButton: tc('actions.cancel'),
    submitButton: tc('actions.create'),
    updateButton: tc('actions.update'),
    viewCard: tc('filters.viewCard'),
    viewTable: tc('filters.viewTable'),
    clearSearch: tc('filters.clear'),
  }

  const icon = (
    <svg className="h-7 w-7 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  )

  const createBatch = async (data: BatchFormData) => {
    let assetId = data.asset_id

    if (assetId && assetId.startsWith('template:')) {
      const templateId = assetId.split(':', 2)[1]
      const created = await createAssetFromTemplate(unitId, templateId)
      assetId = created.id
    }

    const initialQuantity = parseFloat(data.initial_quantity || '0')
    const totalCost = data.total_cost ? parseFloat(data.total_cost) : null

    let unitCost: number | null = data.unit_cost ? parseFloat(data.unit_cost) : null

    if ((unitCost == null || isNaN(unitCost)) && totalCost != null && initialQuantity > 0) {
      unitCost = totalCost / initialQuantity
    }

    return createAssetHoldingWithInitialMovement({
      unitId,
      contextKey: CONTEXT_KEY,
      movementTypeKey: INITIAL_MOVEMENT_TYPE,
      occurredAt: data.acquired_at || null,
      holdingPayload: {
        asset_id: assetId,
        party_id: data.party_id || null,
        location_id: data.location_id || null,
        name: data.name.trim() || null,
        initial_quantity: initialQuantity,
        uom: data.uom || 'un',
        acquired_at: data.acquired_at || null,
        started_at: data.started_at || null,
        unit_cost: unitCost,
        total_cost: totalCost,
        attrs: buildBatchAttrs(data),
      },
    })
  }

  const updateBatch = async (id: string, data: BatchFormData) => {
    const existing = await getAssetHolding(id)

    return updateAssetHolding(id, {
      name: data.name.trim() || null,
      party_id: data.party_id || null,
      uom: data.uom || 'un',
      acquired_at: data.acquired_at || null,
      started_at: data.started_at || null,
      unit_cost: data.unit_cost ? parseFloat(data.unit_cost) : null,
      total_cost: data.total_cost ? parseFloat(data.total_cost) : null,
      attrs: {
        ...buildBatchAttrs(data, existing.attrs ?? {}),
        _inventory_movement: {
          context_key: CONTEXT_KEY,
          movement_type_key: 'adjustment',
          description: `Ajuste - ${data.name.trim() || existing.name || existing.id}`,
          status: 'confirmed',
          movement_date: data.acquired_at || new Date().toISOString().slice(0, 10),
          attrs: {
            source: 'asset_holding_batch_edit',
          },
        },
      },
    })
  }

  const deleteBatch = async (id: string) => {
    await deleteAssetHoldingWithMovements(id)
  }

  const applyBatchFilters = (
    items: AssetHolding[],
    searchQuery: string,
    filterValues: any,
  ) => {
    return items.filter((holding) => {
      const displayName = holding.name ?? holding.asset?.name ?? ''

      if (searchQuery.trim() && !matchesSearch(displayName, searchQuery)) {
        return false
      }

      if (filterValues.name?.trim() && !matchesSearch(displayName, filterValues.name)) {
        return false
      }

      if (filterValues.status && holding.status !== filterValues.status) {
        return false
      }

      if (filterValues.asset_subgroup_id && getHoldingSubgroupId(holding) !== filterValues.asset_subgroup_id) {
        return false
      }

      if (filterValues.location_id && holding.location_id !== filterValues.location_id) {
        return false
      }

      return true
    })
  }

  const validateBatchForm = (data: BatchFormData) => {
    const errors: Record<string, string> = {}

    if (!data.asset_id) {
      errors.asset_id = tc('record.fields.assetTypeRequired')
    }

    const initialQuantity = parseFloat(data.initial_quantity)

    if (!data.initial_quantity || isNaN(initialQuantity) || initialQuantity <= 0) {
      errors.initial_quantity = tc('record.fields.quantityRequired')
    }

    if (data.unit_cost && isNaN(parseFloat(data.unit_cost))) {
      errors.unit_cost = tc('record.fields.unitCostInvalid')
    }

    const birthDate = parseDateOnly(data.birth_date)
    const purchaseDate = parseDateOnly(data.acquired_at)

    if (data.birth_date && !birthDate) {
      errors.birth_date = tc('record.fields.birthDateInvalid')
    }

    if (birthDate && purchaseDate && birthDate > purchaseDate) {
      errors.birth_date = tc('record.fields.birthDateAfterPurchase')
    }

    return errors
  }

  const handlePartyCreated = (newParty: Party) => {
    setParties((prev) => [...prev, newParty].sort((a, b) => a.name.localeCompare(b.name, locale)))
  }

  const handleAssetCreated = (asset: AssetOption) => {
    setAssetOptions((prev) => [
      {
        value: asset.id,
        label: asset.name,
        subgroup_id: asset.subgroup_id ?? null,
      },
      ...prev,
    ])
  }

  const resolveLocationCreation = (location: AssetHoldingLocation | null) => {
    locationCreateResolverRef.current?.(location)
    locationCreateResolverRef.current = null
    setShowLocationModal(false)
  }

  const handleCreateLocation = () => {
    if (showLocationModal) return Promise.resolve(null)

    setShowLocationModal(true)
    return new Promise<AssetHoldingLocation | null>((resolve) => {
      locationCreateResolverRef.current = resolve
    })
  }

  const handleLocationCreated = (created: AssetHoldingLocation) => {
    setLocations((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, locale)))
    resolveLocationCreation(created)
  }

  const handleOpenAdjustment = (batch: AssetHolding) => {
    setAdjustmentBatch(batch)
    setAdjustmentFormData({
      new_initial_quantity: batch.initial_quantity != null ? String(batch.initial_quantity) : '',
      new_total_cost: batch.total_cost != null ? String(batch.total_cost) : '',
    })
    setAdjustmentFormErrors({})
    setAdjustmentError(null)
  }

  const handleCloseAdjustment = () => {
    setAdjustmentBatch(null)
    setAdjustmentFormData({
      new_initial_quantity: '',
      new_total_cost: '',
    })
    setAdjustmentFormErrors({})
    setAdjustmentError(null)
  }

  const handleSubmitAdjustment = async () => {
    const errors: Record<string, string> = {}

    if (!adjustmentFormData.new_initial_quantity) {
      errors.new_initial_quantity = tc('adjustment.messages.requiredQuantity')
    } else {
      const quantity = parseFloat(adjustmentFormData.new_initial_quantity)

      if (isNaN(quantity) || quantity < 0) {
        errors.new_initial_quantity = tc('adjustment.messages.invalidQuantity')
      }
    }

    if (adjustmentFormData.new_total_cost) {
      const cost = parseFloat(adjustmentFormData.new_total_cost)

      if (isNaN(cost) || cost < 0) {
        errors.new_total_cost = tc('adjustment.messages.invalidCost')
      }
    }

    if (Object.keys(errors).length > 0) {
      setAdjustmentFormErrors(errors)
      return
    }

    if (!adjustmentBatch) return

    setAdjustmentSubmitting(true)
    setAdjustmentError(null)

    try {
      const newQuantity = parseFloat(adjustmentFormData.new_initial_quantity)
      const newCost = adjustmentFormData.new_total_cost
        ? parseFloat(adjustmentFormData.new_total_cost)
        : null

      await updateAssetHoldingWithAdjustmentMovement({
        unitId,
        contextKey: 'poultry',
        holding: adjustmentBatch,
        newInitialQuantity: newQuantity,
        newTotalCost: newCost,
      })

      handleCloseAdjustment()
      setRefreshKey((value) => value + 1)

    } catch (error) {
      console.error('[BatchesPage] Failed to adjust batch:', error)
      setAdjustmentError(error instanceof Error ? error.message : tc('adjustment.messages.error'))
    } finally {
      setAdjustmentSubmitting(false)
    }
  }

  const handleOpenLocationTransfer = (batch: AssetHolding) => {
    setLocationTransferBatch(batch)
    setLocationTransferFormData({
      location_id: '',
      quantity: batch.quantity != null ? String(batch.quantity) : '',
      occurred_at: new Date().toISOString().slice(0, 10),
      reason: '',
    })
    setLocationTransferFormErrors({})
    setLocationTransferError(null)
  }

  const handleCloseLocationTransfer = () => {
    setLocationTransferBatch(null)
    setLocationTransferFormData({
      location_id: '',
      quantity: '',
      occurred_at: new Date().toISOString().slice(0, 10),
      reason: '',
    })
    setLocationTransferFormErrors({})
    setLocationTransferError(null)
  }

  const handleSubmitLocationTransfer = async () => {
    const errors: Record<string, string> = {}
    const quantity = Number(locationTransferFormData.quantity)

    if (!locationTransferFormData.location_id) {
      errors.location_id = tc('locationTransfer.messages.requiredLocation')
    }

    if (!locationTransferFormData.quantity || !Number.isFinite(quantity) || quantity <= 0) {
      errors.quantity = tc('locationTransfer.messages.invalidQuantity')
    }

    if (locationTransferBatch && quantity > Number(locationTransferBatch.quantity ?? 0)) {
      errors.quantity = tc('locationTransfer.messages.quantityTooHigh')
    }

    if (locationTransferBatch?.location_id === locationTransferFormData.location_id) {
      errors.location_id = tc('locationTransfer.messages.sameLocation')
    }

    if (Object.keys(errors).length > 0) {
      setLocationTransferFormErrors(errors)
      return
    }

    if (!locationTransferBatch) return

    setLocationTransferSubmitting(true)
    setLocationTransferError(null)

    try {
      const targetLocation = locations.find((location) => location.id === locationTransferFormData.location_id)
      await transferAssetHoldingLocation({
        unitId,
        contextKey: CONTEXT_KEY,
        holding: locationTransferBatch,
        toLocationId: locationTransferFormData.location_id,
        toLocationName: targetLocation?.name ?? null,
        quantity,
        occurredAt: locationTransferFormData.occurred_at || null,
        reason: locationTransferFormData.reason || null,
      })

      handleCloseLocationTransfer()
      setRefreshKey((value) => value + 1)
    } catch (error) {
      console.error('[BatchesPage] Failed to transfer location:', error)
      setLocationTransferError(error instanceof Error ? error.message : tc('locationTransfer.messages.error'))
    } finally {
      setLocationTransferSubmitting(false)
    }
  }

  const cardConfig: CardConfig<AssetHolding> = {
    getIcon: () => icon,
    getMedia: (holding) => (
      <BatchMediaCover
        holdingId={holding.id}
        summary={(holding as BatchHolding).mediaSummary}
        label={tc('actions.media')}
        emptyLabel={tc('media.empty')}
        onOpen={() => setMediaBatch(holding)}
      />
    ),
    getTitle: (holding) => holding.name ?? holding.asset?.name ?? holding.id,
    getSubtitle: (holding) => holding.asset?.name ?? '',
    getBadge: (holding) => ({
      label: statusLabels[holding.status] ?? holding.status,
      variant: STATUS_VARIANTS[holding.status] ?? 'info',
    }),
    getFooter: (holding) => (
      <div className="flex items-center gap-1.5">
        {Boolean((holding as BatchHolding).mediaSummary?.count) && (
          <BatchMediaIconButton
            count={(holding as BatchHolding).mediaSummary?.count ?? 0}
            label={tc('actions.media')}
            onOpen={() => setMediaBatch(holding)}
          />
        )}
        {Boolean((holding as BatchHolding).attachmentSummary?.count) && (
          <BatchAttachmentIconButton
            count={(holding as BatchHolding).attachmentSummary?.count ?? 0}
            label={tc('actions.attachments')}
            onOpen={() => setAttachmentBatch(holding)}
          />
        )}
      </div>
    ),
    fields: [
      {
        key: 'quantity',
        label: tc('list.columns.quantity'),
        getValue: (holding) =>
          `${Number(holding.quantity).toLocaleString(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
          })} ${holding.uom}`,
      },
      {
        key: 'asset_subgroup',
        label: tc('record.fields.assetSubgroup'),
        getValue: getHoldingSubgroupLabel,
        isVisible: (holding) => Boolean(getHoldingSubgroupId(holding)),
      },
      {
        key: 'initial_quantity',
        label: tc('list.columns.initial_quantity'),
        isVisible: () => false,
        getValue: (holding) =>
          holding.initial_quantity != null
            ? `${Number(holding.initial_quantity).toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 3,
              })} ${holding.uom}`
            : '—',
      },
      {
        key: 'acquired_at',
        label: tc('record.fields.acquiredAt'),
        isVisible: () => false,
        getValue: (holding) =>
          holding.acquired_at
            ? new Date(`${holding.acquired_at}T00:00:00`).toLocaleDateString(locale)
            : '—',
      },
      {
        key: 'party',
        label: tc('record.fields.party'),
        getValue: (holding) => holding.party?.name ?? '—',
        isVisible: (holding) => !!holding.party,
      },
      {
        key: 'location',
        label: tc('record.fields.location'),
        getValue: (holding) => holding.location?.name ?? 'â€”',
        isVisible: (holding) => !!holding.location,
      },
      {
        key: 'age',
        label: tc('list.columns.age'),
        getValue: formatBatchAge,
        isVisible: hasBirthDate,
      },
      {
        key: 'media',
        label: tc('list.columns.media'),
        isVisible: () => false,
        getValue: (holding) => (
          <EntityMediaIndicator
            summary={(holding as BatchHolding).mediaSummary}
            label={tc('actions.media')}
            emptyLabel={tc('media.empty')}
            onOpen={() => setMediaBatch(holding)}
          />
        ),
      },
      {
        key: 'attachments',
        label: tc('list.columns.attachments'),
        isVisible: () => false,
        getValue: (holding) => (
          <BatchAttachmentIndicator
            count={(holding as BatchHolding).attachmentSummary?.count ?? 0}
            label={tc('actions.attachments')}
            onOpen={() => setAttachmentBatch(holding)}
          />
        ),
      },
      {
        key: 'unit_cost',
        label: tc('record.fields.unitCost'),
        getValue: (holding) =>
          holding.unit_cost != null
            ? Number(holding.unit_cost).toLocaleString(locale, {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 4,
              })
            : '—',
        isVisible: (holding) => holding.unit_cost != null,
      },
    ],
    editLabel: tc('actions.update'),
    deleteLabel: tc('actions.delete'),
    getExtraActions: (batch) => [
      {
        label: tc('actions.transferLocation'),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 6l-6 6m0 0l6 6m-6-6h18" />
          </svg>
        ),
        onClick: () => handleOpenLocationTransfer(batch),
        variant: 'info' as const,
      },
      {
        label: tc('actions.adjust'),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        ),
        onClick: () => handleOpenAdjustment(batch),
        variant: 'warning' as const,
      },
    ],
  }

  const renderBatchForm = (
    formData: BatchFormData,
    setFormData: (data: BatchFormData) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    editingEntity: AssetHolding | null,
    mode: 'create' | 'edit' | 'view',
  ) => {
    return (
      <BatchFormModal
        unitId={unitId}
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        submitting={submitting}
        editingEntity={editingEntity}
        mode={mode}
        assetOptions={assetOptions}
        assetSubgroups={assetSubgroups}
        assetsLoading={assetsLoading}
        parties={parties}
        locations={locations}
        onAssetCreated={handleAssetCreated}
        onPartyCreated={handlePartyCreated}
        onOpenAssetModal={(subgroupId) => {
          setAssetModalSubgroupId(subgroupId ?? '')
          setShowAssetModal(true)
        }}
        onCreateLocation={handleCreateLocation}
      />
    )
  }

  const [refreshKey, setRefreshKey] = useState(0)

  return (
    <>
      <AssetHoldingListPage<BatchFormData>
        entityType="batches"
        refreshKey={refreshKey}
        labels={labels}
        icon={icon}
        filterFields={filterFields}
        tableColumns={tableColumns}
        cardConfig={cardConfig}
        cardCustomizationStorageKey="poultry-batches-cards-v2"
        defaultCardTemplate="image-focus"
        preferDefaultCardTemplate
        viewModeStorageKey="poultry-batches-view-mode-v2"
        defaultViewMode="card"
        preferDefaultViewMode
        loadData={() => loadBatchesWithMedia(unitId)}
        createEntity={createBatch}
        updateEntity={updateBatch}
        deleteEntity={deleteBatch}
        applyFilters={applyBatchFilters}
        validateForm={validateBatchForm}
        renderForm={renderBatchForm}
        entityToFormData={batchToFormData}
        emptyFormData={emptyBatchFormData}
      />

      {showAssetModal && (
        <AssetFormModal
          unitId={unitId}
          isOpen={showAssetModal}
          assetSubgroups={assetSubgroups}
          initialSubgroupId={assetModalSubgroupId}
          onClose={() => setShowAssetModal(false)}
          onCreated={(asset) => {
            handleAssetCreated(asset)
            setShowAssetModal(false)
          }}
        />
      )}

      <AssetHoldingLocationFormModal
        unitId={unitId}
        isOpen={showLocationModal}
        onClose={() => resolveLocationCreation(null)}
        onCreated={handleLocationCreated}
        messages={{
          title: tc('locationModal.title'),
          submitTitle: tc('locationModal.submitTitle'),
          nameLabel: tc('locationModal.fields.name'),
          namePlaceholder: tc('locationModal.fields.namePlaceholder'),
          nameRequired: tc('locationModal.fields.nameRequired'),
          descriptionLabel: tc('locationModal.fields.description'),
          descriptionPlaceholder: tc('locationModal.fields.descriptionPlaceholder'),
          error: tc('locationModal.messages.error'),
          closeLabel: tc('actions.cancel'),
        }}
      />

      <BatchAdjustmentModal
        isOpen={!!adjustmentBatch}
        onClose={handleCloseAdjustment}
        onSubmit={handleSubmitAdjustment}
        title={tc('adjustment.title')}
        submitLabel={tc('adjustment.submit')}
        unitId={unitId}
        batch={adjustmentBatch}
        formData={adjustmentFormData}
        setFormData={setAdjustmentFormData}
        formErrors={adjustmentFormErrors}
        submitting={adjustmentSubmitting}
        error={adjustmentError}
        tc={(key) => tc(`adjustment.${key}`)}
      />

      <BatchLocationTransferModal
        isOpen={!!locationTransferBatch}
        onClose={handleCloseLocationTransfer}
        onSubmit={handleSubmitLocationTransfer}
        title={tc('locationTransfer.title')}
        submitLabel={tc('locationTransfer.submit')}
        batch={locationTransferBatch}
        locations={locations}
        formData={locationTransferFormData}
        setFormData={setLocationTransferFormData}
        formErrors={locationTransferFormErrors}
        submitting={locationTransferSubmitting}
        error={locationTransferError}
        onCreateLocation={async () => {
          const created = await handleCreateLocation()
          if (created) {
            setLocationTransferFormData((prev) => ({ ...prev, location_id: created.id }))
          }
        }}
        tc={(key) => tc(`locationTransfer.${key}`)}
      />

      {mediaBatch && (
        <BatchMediaModal
          batch={mediaBatch}
          onClose={() => {
            setMediaBatch(null)
            setRefreshKey((value) => value + 1)
          }}
        />
      )}

      {attachmentBatch && (
        <BatchAttachmentModal
          batch={attachmentBatch}
          onClose={() => {
            setAttachmentBatch(null)
            setRefreshKey((value) => value + 1)
          }}
        />
      )}
    </>
  )
}

function buildBatchAttrs(data: BatchFormData, existingAttrs: Record<string, any> = {}) {
  return {
    ...existingAttrs,
    [BIRTH_DATE_ATTR]: data.birth_date || null,
    [BIRTH_DATE_SOURCE_ATTR]: data.birth_date ? 'exact' : null,
    [PURCHASE_AGE_MONTHS_ATTR]: 0,
    [PURCHASE_AGE_DAYS_ATTR]: 0,
  }
}

function parseDateOnly(value: string | null | undefined) {
  if (!value) return null

  const [year, month, day] = value.slice(0, 10).split('-').map(Number)

  if (!year || !month || !day) return null

  return new Date(year, month - 1, day)
}

function formatDateOnly(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function subtractMonthsAndDays(date: Date, months: number, days: number) {
  const result = new Date(date)
  result.setMonth(result.getMonth() - months)
  result.setDate(result.getDate() - days)

  return result
}

function getHoldingBirthDate(holding: AssetHolding) {
  const storedBirthDate = holding.attrs?.[BIRTH_DATE_ATTR]

  if (storedBirthDate) return String(storedBirthDate)

  const purchaseDate = parseDateOnly(holding.acquired_at)
  const months = Number(holding.attrs?.[PURCHASE_AGE_MONTHS_ATTR] ?? 0)
  const days = Number(holding.attrs?.[PURCHASE_AGE_DAYS_ATTR] ?? 0)

  if (!purchaseDate || (!months && !days)) return null

  return formatDateOnly(subtractMonthsAndDays(purchaseDate, months, days))
}

function calculateAgeParts(fromDate: Date, toDate: Date) {
  let years = toDate.getFullYear() - fromDate.getFullYear()
  let months = toDate.getMonth() - fromDate.getMonth()
  let days = toDate.getDate() - fromDate.getDate()

  if (days < 0) {
    const previousMonthLastDay = new Date(toDate.getFullYear(), toDate.getMonth(), 0).getDate()
    days += previousMonthLastDay
    months -= 1
  }

  if (months < 0) {
    months += 12
    years -= 1
  }

  return { years, months, days }
}

function formatCurrentAge(
  holding: AssetHolding,
  labels: { year: string; years: string; month: string; months: string; day: string; days: string; separator: string },
) {
  const birthDate = parseDateOnly(getHoldingBirthDate(holding))

  if (!birthDate) return 'â€”'

  const { years, months, days } = calculateAgeParts(birthDate, new Date())
  const parts: string[] = []

  if (years) parts.push(`${years} ${years === 1 ? labels.year : labels.years}`)
  if (months) parts.push(`${months} ${months === 1 ? labels.month : labels.months}`)
  if (days || parts.length === 0) parts.push(`${days} ${days === 1 ? labels.day : labels.days}`)

  return parts.join(` ${labels.separator} `)
}

function hasBirthDate(holding: AssetHolding) {
  return Boolean(getHoldingBirthDate(holding))
}

function formatPurchaseAge(
  holding: AssetHolding,
  labels: { month: string; months: string; day: string; days: string; separator: string },
) {
  const months = Number(holding.attrs?.[PURCHASE_AGE_MONTHS_ATTR] ?? 0)
  const days = Number(holding.attrs?.[PURCHASE_AGE_DAYS_ATTR] ?? 0)

  if (!months && !days) return '—'

  const parts = []

  if (months) parts.push(`${months} ${months === 1 ? labels.month : labels.months}`)
  if (days) parts.push(`${days} ${days === 1 ? labels.day : labels.days}`)

  return parts.join(` ${labels.separator} `)
}

function hasPurchaseAge(holding: AssetHolding) {
  return Boolean(
    Number(holding.attrs?.[PURCHASE_AGE_MONTHS_ATTR] ?? 0) ||
    Number(holding.attrs?.[PURCHASE_AGE_DAYS_ATTR] ?? 0),
  )
}

async function loadBatchesWithMedia(unitId: string): Promise<AssetHolding[]> {
  const holdings = await listPoultryHoldings(unitId)
  const holdingIds = holdings.map((holding) => holding.id)
  const [mediaSummaries, attachmentSummaries] = await Promise.all([
    getBatchMediaSummaries(holdingIds),
    getBatchAttachmentSummaries(holdingIds),
  ])

  return holdings.map((holding) => ({
    ...holding,
    mediaSummary: mediaSummaries[holding.id] ?? { count: 0, previews: [] },
    attachmentSummary: attachmentSummaries[holding.id] ?? { count: 0, previews: [] },
  }))
}

function BatchMediaCover({
  holdingId,
  summary,
  label,
  emptyLabel,
  onOpen,
}: {
  holdingId: string
  summary?: BatchMediaSummary
  label: string
  emptyLabel: string
  onOpen: () => void
}) {
  const summaryPreviews = summary?.previews ?? []
  const totalCount = summary?.count ?? 0
  const [loadedPreviews, setLoadedPreviews] = useState<BatchMediaPreview[] | null>(null)
  const [loadingPreviews, setLoadingPreviews] = useState(false)
  const previews = loadedPreviews ?? summaryPreviews
  const [activeIndex, setActiveIndex] = useState(0)
  const activePreview = previews[Math.min(activeIndex, Math.max(previews.length - 1, 0))]

  useEffect(() => {
    setLoadedPreviews(null)
    setActiveIndex(0)
  }, [holdingId, summary?.count, summaryPreviews[0]?.id])

  const loadAllPreviews = async () => {
    if (loadedPreviews || loadingPreviews) return loadedPreviews ?? previews

    setLoadingPreviews(true)
    try {
      const items = await getBatchMedia(holdingId)
      const fullPreviews = await Promise.all(items.map(async (item) => ({
        id: item.id,
        entity_id: item.entity_id,
        media_name: item.media_name,
        media_type: item.media_type,
        media_url: item.media_url,
        signed_url: await getBatchMediaUrl(item.media_url),
        metadata: item.metadata,
        created_at: item.created_at,
      } satisfies BatchMediaPreview)))

      setLoadedPreviews(fullPreviews)
      return fullPreviews
    } finally {
      setLoadingPreviews(false)
    }
  }

  const handleSelect = async (event: MouseEvent, index: number) => {
    event.stopPropagation()
    if (!loadedPreviews && totalCount > summaryPreviews.length) {
      await loadAllPreviews()
    }
    setActiveIndex(index)
  }

  const handlePrevious = async (event: MouseEvent) => {
    event.stopPropagation()
    const allPreviews = await loadAllPreviews()
    if (allPreviews.length <= 1) return
    setActiveIndex((index) => (index === 0 ? allPreviews.length - 1 : index - 1))
  }

  const handleNext = async (event: MouseEvent) => {
    event.stopPropagation()
    const allPreviews = await loadAllPreviews()
    if (allPreviews.length <= 1) return
    setActiveIndex((index) => (index + 1) % allPreviews.length)
  }

  const openMedia = (event: MouseEvent) => {
    event.stopPropagation()
    onOpen()
  }

  const openPreview = (event: MouseEvent) => {
    event.stopPropagation()

    if (activePreview?.signed_url) {
      window.open(activePreview.signed_url, '_blank', 'noopener,noreferrer')
      return
    }

    onOpen()
  }

  if (!activePreview) {
    return (
      <button
        type="button"
        onClick={openMedia}
        className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-amber-50 via-white to-sky-50 text-slate-500 transition-colors hover:text-amber-700 dark:from-amber-950/20 dark:via-slate-900 dark:to-sky-950/20 dark:text-slate-400 dark:hover:text-amber-300"
        title={emptyLabel}
      >
        <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/85 text-sky-600 shadow-sm ring-1 ring-slate-200/80 dark:bg-slate-800/85 dark:text-sky-300 dark:ring-slate-700">
          <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 16l4.2-4.2a2 2 0 012.8 0l2 2 1.2-1.2a2 2 0 012.8 0L20 15.6M8 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="absolute -right-1 -top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-600 text-white shadow-sm ring-2 ring-white dark:bg-sky-400 dark:text-slate-950 dark:ring-slate-800">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v14m7-7H5" />
            </svg>
          </span>
        </span>
        <span className="text-xs font-medium">{emptyLabel}</span>
      </button>
    )
  }

  return (
    <div className="relative h-full w-full bg-slate-900">
      <button
        type="button"
        onClick={openPreview}
        className="block h-full w-full"
        title={activePreview.media_name || label}
      >
        <BatchMediaCoverPreview preview={activePreview} />
      </button>

      {totalCount ? (
        <span className="absolute right-3 top-3 rounded-full bg-slate-950/70 px-2 py-1 text-xs font-semibold text-white shadow-sm">
          {totalCount}
        </span>
      ) : null}

      {totalCount > 1 && (
        <>
          <button
            type="button"
            onClick={handlePrevious}
            className="absolute left-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-slate-950/55 text-white shadow-sm transition hover:bg-slate-950/75 disabled:cursor-wait disabled:opacity-60"
            title={label}
            aria-label={label}
            disabled={loadingPreviews}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            type="button"
            onClick={handleNext}
            className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-slate-950/55 text-white shadow-sm transition hover:bg-slate-950/75 disabled:cursor-wait disabled:opacity-60"
            title={label}
            aria-label={label}
            disabled={loadingPreviews}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="absolute inset-x-3 bottom-3 flex items-center justify-center gap-1.5">
          {Array.from({ length: totalCount }).map((_, index) => {
            const preview = previews[index]
            return (
            <button
              key={preview?.id ?? index}
              type="button"
              onClick={(event) => handleSelect(event, index)}
              className={`h-2.5 rounded-full transition ${
                index === activeIndex
                  ? 'w-5 bg-white'
                  : 'w-2.5 bg-white/55 hover:bg-white/80'
              }`}
              title={preview?.media_name ?? label}
              aria-label={preview?.media_name ?? label}
            />
            )
          })}
          </div>
        </>
      )}
    </div>
  )
}

function BatchMediaCoverPreview({
  preview,
  compact = false,
}: {
  preview: BatchMediaPreview
  compact?: boolean
}) {
  if (preview.media_type === 'image' && preview.signed_url) {
    return (
      <img
        src={preview.signed_url}
        alt={preview.media_name}
        className="h-full w-full object-cover"
      />
    )
  }

  return (
    <span className={`flex h-full w-full items-center justify-center bg-slate-800 text-slate-200 ${compact ? '' : 'gap-2'}`}>
      <svg className={compact ? 'h-5 w-5' : 'h-10 w-10'} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
      {!compact && <span className="text-sm font-medium">{preview.media_name}</span>}
    </span>
  )
}

function BatchAttachmentIndicator({
  count,
  label,
  onOpen,
}: {
  count: number
  label: string
  onOpen: () => void
}) {
  if (!count) {
    return <span className="text-slate-400">—</span>
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onOpen()
      }}
      className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
      title={label}
    >
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
      </svg>
      <span>{count}</span>
    </button>
  )
}

function BatchMediaIconButton({
  count,
  label,
  onOpen,
}: {
  count: number
  label: string
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onOpen()
      }}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-sky-700 transition-colors hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-900/30"
      title={`${label} (${count})`}
      aria-label={`${label} (${count})`}
    >
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </button>
  )
}

function BatchAttachmentIconButton({
  count,
  label,
  onOpen,
}: {
  count: number
  label: string
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onOpen()
      }}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-indigo-700 transition-colors hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
      title={`${label} (${count})`}
      aria-label={`${label} (${count})`}
    >
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
      </svg>
    </button>
  )
}

function BatchMediaIndicator({
  batch,
  label,
  emptyLabel,
  onOpen,
}: {
  batch: BatchHolding
  label: string
  emptyLabel: string
  onOpen: () => void
}) {
  const summary = batch.mediaSummary
  const count = summary?.count ?? 0
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [previewPosition, setPreviewPosition] = useState<{ top: number; left: number } | null>(null)

  const showPreview = () => {
    const button = buttonRef.current
    if (!button) return

    const rect = button.getBoundingClientRect()
    const width = 224
    const estimatedHeight = 126
    const margin = 8
    const left = Math.min(
      Math.max(rect.left, margin),
      Math.max(window.innerWidth - width - margin, margin),
    )
    const belowTop = rect.bottom + margin
    const top = belowTop + estimatedHeight > window.innerHeight
      ? Math.max(rect.top - estimatedHeight - margin, margin)
      : belowTop

    setPreviewPosition({ top, left })
  }

  if (!count) {
    return <span className="text-slate-400">—</span>
  }

  return (
    <>
    <button
      ref={buttonRef}
      type="button"
      onMouseEnter={showPreview}
      onMouseLeave={() => setPreviewPosition(null)}
      onFocus={showPreview}
      onBlur={() => setPreviewPosition(null)}
      onClick={(event) => {
        event.stopPropagation()
        onOpen()
      }}
      className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 dark:bg-sky-900/30 px-2 py-1 text-xs font-medium text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors"
      title={label}
    >
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <span>{count}</span>

    </button>

    {previewPosition && createPortal(
      <div
        className="pointer-events-none fixed z-[100] w-56 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 shadow-xl"
        style={{ top: previewPosition.top, left: previewPosition.left }}
      >
        <span className="mb-2 block text-xs font-semibold text-slate-700 dark:text-slate-200">
          {label} ({count})
        </span>
        {summary?.previews.length ? (
          <span className="grid grid-cols-3 gap-2">
            {summary.previews.map((preview) => (
              <span
                key={preview.id}
                className="flex aspect-square items-center justify-center overflow-hidden rounded-md bg-slate-100 dark:bg-slate-900"
                title={preview.media_name}
              >
                {preview.media_type === 'image' && preview.signed_url ? (
                  <img
                    src={preview.signed_url}
                    alt={preview.media_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <svg className="h-7 w-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </span>
            ))}
          </span>
        ) : (
          <span className="text-xs text-slate-500 dark:text-slate-400">{emptyLabel}</span>
        )}
      </div>,
      document.body,
    )}
    </>
  )
}

function emptyBatchFormData(): BatchFormData {
  const todayDate = new Date()
  const yesterdayDate = new Date(todayDate)
  yesterdayDate.setDate(yesterdayDate.getDate() - 1)

  const today = todayDate.toISOString().slice(0, 10)
  const yesterday = yesterdayDate.toISOString().slice(0, 10)

  return {
    name: '',
    asset_id: '',
    asset_subgroup_id: '',
    party_id: '',
    location_id: '',
    initial_quantity: '',
    uom: 'un',
    acquired_at: today,
    started_at: today,
    unit_cost: '',
    total_cost: '',
    birth_date: yesterday,
  }
}

function batchToFormData(holding: AssetHolding): BatchFormData {
  return {
    name: holding.name ?? '',
    asset_id: holding.asset_id,
    asset_subgroup_id: holding.asset?.subgroup_id ?? '',
    party_id: holding.party_id ?? '',
    location_id: holding.location_id ?? '',
    initial_quantity: holding.initial_quantity != null ? String(holding.initial_quantity) : '',
    uom: holding.uom,
    acquired_at: holding.acquired_at ?? '',
    started_at: holding.started_at ?? '',
    unit_cost: holding.unit_cost != null ? String(holding.unit_cost) : '',
    total_cost: holding.total_cost != null ? String(holding.total_cost) : '',
    birth_date: getHoldingBirthDate(holding) ?? '',
  }
}
